import { setActiveAccount } from '@customerio/hydra-nav-state';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getWorkspaceIdFromUrl, useCurrentWorkspace } from '../../context/current-workspace';
import { analytics } from '../analytics/client';
import { getMasqId, selectSessionForMasq } from '../../utils/masq';
import { getRoleDefinitions } from '@/features/roles';
import { augmentAccount, augmentUser } from './augment';
import { session } from '@/services/session';
import { setAccountStore, setUserStore, setWorkspaceStore } from './store';
import type { Account, AugmentedAccount, AugmentedUser, RawAccount, RawUser, User, Workspace } from './types';
import { Region } from './types';
import { fetchAccountById, fetchAccountByEnvironment, fetchCurrentAccount } from './account-api';
import { isSessionRejection, toAuthResolutionError } from './auth-resolution-error';
import { UserMasqPermission } from '@customerio/hydra-permissions/masq';

type Auth = {
  account: AugmentedAccount | null;
  user: AugmentedUser | null;
  workspaces: Workspace[] | null;
};

// Monotonic count of successful authentications (local `authenticate()` calls
// and cross-tab broadcasts both emit `authenticated`). A rejected auth fetch
// only syncs `session.isAuthenticated` to `false` when no authentication
// happened after the fetch started — otherwise a stale 401 straggler (a
// request that left before an in-place re-auth completed) would log the
// freshly re-authenticated session back out.
let authEpoch = 0;
session.on('authenticated', () => {
  authEpoch += 1;
});

type AuthMethod = {
  /**
   * True when the selected fetch resolves a masqueraded account
   * (`?masq_account_id`). Captured at selection time so error handling can
   * tell a rejected masq target apart from a rejected session without
   * re-reading the URL, which may have changed by the time the fetch settles.
   */
  isMasqFetch: boolean;
  fetch: () => Promise<{ account: Account | null; user: User | null; workspaces: Workspace[] | null }>;
};

function getAuthMethod(workspaceId: string | null): AuthMethod {
  const currentUrl = window.location.href;

  const masqAccountId = getMasqId(currentUrl);
  if (masqAccountId != null && !Number.isNaN(Number.parseInt(masqAccountId))) {
    return {
      isMasqFetch: true,
      fetch: async () => {
        return fetchAccountById(Number.parseInt(masqAccountId)).then((response) => {
          return {
            account: getAccountFromRaw(response.account),
            user: getUserFromRaw(response.account, response.user),
            workspaces: response.environments,
          };
        });
      },
    };
  }

  if (workspaceId != null && !Number.isNaN(Number.parseInt(workspaceId))) {
    return {
      isMasqFetch: false,
      fetch: async () => {
        return fetchAccountByEnvironment(Number.parseInt(workspaceId)).then((response) => {
          return {
            account: getAccountFromRaw(response.account),
            user: getUserFromRaw(response.account, response.user),
            workspaces: response.environments,
          };
        });
      },
    };
  }

  return {
    isMasqFetch: false,
    fetch: async () => {
      return await fetchCurrentAccount().then((response) => {
        return {
          account: getAccountFromRaw(response.account),
          user: getUserFromRaw(response.account, response.user),
          workspaces: response.environments,
        };
      });
    },
  };
}

/**
 * A query that checks if the user is authenticated and retrieves the access token.
 *
 * In most cases you want to use the `useAuth` hook instead of this query directly.
 *
 * Gated on `session.isInitialized`: the singleton `Session` in
 * `@/services/session` fetches `/accounts/current` (or the masq/workspace
 * variants) on app boot, retries on network failures, and seeds the
 * cross-app cache that the `account-api` fetchers read from. Waiting on
 * `isInitialized` ensures the cache is populated before this query runs, so
 * the happy path is a synchronous cache hit with no second network call and
 * no in-hook retries are needed.
 */
export function useAuthQuery() {
  const currentWorkspace = useCurrentWorkspace();
  const queryClient = useQueryClient();
  const workspaceIdFromUrl = getWorkspaceIdFromUrl(window.location.href);
  const workspaceId = workspaceIdFromUrl ?? (currentWorkspace?.id != null ? String(currentWorkspace.id) : null);

  return useQuery({
    retry: false,
    queryKey: ['authenticated'],
    queryFn: async () => {
      const { isMasqFetch, fetch: fetchAccount } = getAuthMethod(workspaceId);
      const epochAtStart = authEpoch;
      try {
        void (await session.initialized);
        const auth: Auth = { account: null, user: null, workspaces: null };
        const result = await fetchAccount();

        if (result.account != null) {
          if (result.account.is_sample_account_access && result.user != null) {
            const { account: a } = await fetchAccountById(result.user.account_id);
            auth.account = augmentAccount(getAccountFromRaw(a));
            setAccountStore(auth.account);
          } else {
            auth.account = augmentAccount(result.account);
            setAccountStore(auth.account);
          }
          selectSessionForMasq();
        }

        if (result.user != null) {
          auth.user = augmentUser(result.user);
          setUserStore(auth.user);
        }

        if (result.workspaces != null) {
          auth.workspaces = result.workspaces;
          setWorkspaceStore(auth.workspaces);
        }

        assignAuthInfoGlobal(auth);
        updateActiveAccount(auth);

        // Prefetch roles so role/permission data is available from one place.
        // Do not block auth on roles; populate the cache for `useRoleDefinitions` consumers.
        if (auth.account?.id != null) {
          void queryClient.prefetchQuery({
            queryKey: ['role_definitions', auth.account.id],
            queryFn: async () => {
              return await getRoleDefinitions(auth.account!.id);
            },
            staleTime: Infinity,
          });
        }

        return auth;
      } catch (error) {
        const resolutionError = toAuthResolutionError(error, isMasqFetch);

        // A session that expires server-side leaves `session.isAuthenticated`
        // latched at the boot-time `true`: the store only changes on boot or an
        // explicit `authenticate()`/`invalidate()`, and server-side expiry
        // emits neither. The login page trusts that store to bounce
        // authenticated visitors home, so a stale `true` sends an expired tab
        // straight back into the authenticated tree, which remounts this
        // query, 401s again, and ping-pongs indefinitely — hammering
        // `/v1/account_by_environment` (FPS-2996). Sync the store when the
        // server rejects the session so the login page renders the form.
        //
        // Deliberately a plain store write, not `session.invalidate()`:
        // invalidate broadcasts to every same-slot tab and fires the
        // hard-redirect in `services/session/invalidation-redirect.ts`, which
        // would tear down other tabs mid-work (including the embedded app's
        // in-place re-auth modal) and preempt the authenticated layout's
        // expired-session fallbacks. Each tab syncs itself on its own next
        // auth rejection instead.
        //
        // Masq fetches are excluded (`isSessionRejection` is false for them):
        // a rejected masq target says nothing about the admin's own session,
        // and bouncing home is the intended recovery for a bad masq target.
        // The epoch check drops stale stragglers — see `authEpoch`.
        if (isSessionRejection(resolutionError) && epochAtStart === authEpoch) {
          session.isAuthenticated.set(false);
        }

        // Throw (rather than resolve `null`) so react-query keeps the cached
        // last-good auth data and exposes the classified failure as `error` —
        // the layout routes on it, and the retained data is what an in-place
        // re-auth can render the app from.
        throw resolutionError;
      }
    },
  });
}

function updateActiveAccount(auth: Auth) {
  const regionId = auth.account?.region;
  let regionName: string | undefined;
  if (regionId != null) {
    regionName = regionId === Region.EU ? 'eu' : 'us';
  }
  setActiveAccount({
    id: auth.account?.id,
    regionId,
    regionName,
  });
}

// Exposes auth info for external scripts like analytics trackers.
function assignAuthInfoGlobal(auth: Auth) {
  Object.assign(window, {
    __hydra__: {
      ...window.__hydra__,
      account: {
        id: auth.account?.id,
        dc: auth.account?.region === Region.EU ? 'eu' : 'us',
        ordway_id: auth.account?.ordway_id,
        account_state: auth.account?.account_state,
        billing_vendor: auth.account?.billing_vendor,
      },
      user: {
        id: auth.user?.id,
        email: auth.user?.email,
      },
      analytics,
    } satisfies Window['__hydra__'],
  });
}

// TODO: would be great if someone cleaned this up someday
function getAccountFromRaw(account: RawAccount): Account {
  return {
    id: +account.id,
    created_at: account.created_at,
    name: account.name,
    email: account.email,
    is_sample_account_access: account.is_sample_account_access,
    is_masq: account.is_masqueraded,
    is_paused: account.is_paused,
    write_access:
      !account.is_sample_account_access && !account.is_masqueraded && !account.read_only && !account.is_paused,
    is_own_account: !account.is_masqueraded && !account.is_sample_account_access,
    feature_flags: account.feature_flags,
    logo_url: account.logo_url,
    plan_slug: account.plan_slug,
    premium: account.premium,
    premium_trial: account.premium_trial,
    plan_features: [],
    gated_features: account.gated_features,
    admin: account.admin,
    product_activations: account.product_activations ?? [],
    region: account.data_center === 'eu' ? Region.EU : Region.US,
    support_tier: account.support_tier,
    suspend_trial_at: account.suspend_trial_at,
    delete_at: account.delete_at,
    account_state: account.account_state,
    sso_setting: account.sso_setting,
    plan_period_start: account.plan_period_start,
    plan_period_end: account.plan_period_end,
    plan_include_churn: account.plan_include_churn,
    address_line1: account.address_line1,
    address_line2: account.address_line2,
    city: account.city,
    state: account.state,
    zip_code: account.zip_code,
    country: account.country,
    access_granted_until: account.access_granted_until,
    access_granted_by: account.access_granted_by,
    access_granted_on: account.access_granted_on,
    data_center: account.data_center,
    enable_troubleshooting_cookies: account.enable_troubleshooting_cookies,
    has_csm: account.has_csm,
    cs_access_granted_until: account.cs_access_granted_until,
    cs_access_granted_by: account.cs_access_granted_by,
    cs_access_granted_on: account.cs_access_granted_on,
    ordway_id: account.ordway_id,
    billing_vendor: account.billing_vendor,
    promo_codes: account.promo_codes,
    read_only: account.read_only,
    confirmed: account.confirmed,
    verification_state: account.verification_state,
    verification_review_state: account.verification_review_state,
    sandbox: account.sandbox === true,
    partner_account: account.partner_account === true,
    public_settings: account.public_settings,
    signup_profile: account.signup_profile,
  };
}

// TODO: would be great if someone cleaned this up someday
function getUserFromRaw(account: RawAccount, user: RawUser): User {
  return {
    id: +user.id,
    first_name: user.first_name,
    last_name: user.last_name,
    email: user.email,
    account_id: +account.id,
    last_cio_workspace_id: user.last_environment_id,
    account_permissions: user.account_permissions,
    accessible_environment_ids: user.accessible_environment_ids ?? [],
    roles: user.roles ?? [],
    masq_permissions: user.masq_permissions ?? UserMasqPermission.NoMasqAccess,
    can_masquerade: user.can_masquerade ?? false,
    can_masquerade_write: user.can_masquerade_write ?? false,
    user_flags: user.user_flags,
    settings: user.settings,
    google_drive_credential: user.google_drive_credential ?? null,
    external_identity_provider: user.external_identity_provider ?? '',
    external_identity_email: user.external_identity_email ?? '',
    two_factor_authentication_enabled: user.two_factor_authentication_enabled ?? false,
    profile_image_url: user.profile_image_url ?? null,
    two_factor_authentication_email_enabled: user.two_factor_authentication_email_enabled ?? false,
  };
}
